// The Scenarios list + editor Drawer. Scoped to one agent when the page
// carries `?agent=`, else the working team's list (`team_id`, docs/09 §4 —
// the personas pattern; `GET /v1/scenarios` takes `agent_id` as a query
// filter, never a path parent). Container owns the queries + mutations;
// `ScenariosListView` is props-only for tests/stories.
import { useMemo, useState } from "react";

import {
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";
import type { components } from "../../api/schema";
import { apiClient } from "../../lib/apiClient";
import { ApiRequestError, requestErrorDescription } from "../../lib/errors";
import { queryKeys } from "../../lib/queryKeys";
import { useListQuery, type ListQueryPage } from "../../lib/useListQuery";
import { useResolvedWorkingTeamId } from "../../lib/workingScope";
import { useToast } from "../../ui";
import type {
  ScenarioAgentOption,
  ScenarioFormSubmission,
} from "./ScenarioEditor";
import {
  ScenariosListView,
  type ScenarioDrawerState,
} from "./ScenariosListView";

type ScenarioCreate = components["schemas"]["ScenarioCreate"];
type ScenarioResponse = components["schemas"]["ScenarioResponse"];
type ScenarioRevise = components["schemas"]["ScenarioRevise"];
export interface ScenariosListProps {
  // Present = the page carries an agent scope; absent = all scenarios.
  agentId?: string;
  editingScenarioId: string | null;
  isCreateOpen: boolean;
  onCloseEditor: () => void;
  onOpenEdit: (scenarioId: string) => void;
}

export function ScenariosList({
  agentId,
  editingScenarioId,
  isCreateOpen,
  onCloseEditor,
  onOpenEdit,
}: ScenariosListProps) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [deleteScenarioId, setDeleteScenarioId] = useState<string | null>(null);
  // All agents = the working team's scenarios (docs/09 §4); held until the
  // shell resolves the team. Agent wins: never both filters.
  const workingTeamId = useResolvedWorkingTeamId();
  const scenariosListKey =
    agentId != null
      ? queryKeys.scenarios.list({ agent_id: agentId })
      : queryKeys.scenarios.list({ team_id: workingTeamId });
  const scenarios = useListQuery("/v1/scenarios", {
    enabled: agentId != null || workingTeamId !== undefined,
    params:
      agentId != null ? { agent_id: agentId } : { team_id: workingTeamId },
    queryKey: scenariosListKey,
  });

  // Unscoped views resolve agent display names for the Agent column and the
  // create form's owning-agent Select (the personas precedent).
  const agents = useListQuery("/v1/agents", {
    enabled: agentId === undefined,
    queryKey: queryKeys.agents.list(),
  });
  const agentNamesById = useMemo(
    () => new Map(agents.items.map((agent) => [agent.id, agent.display_name])),
    [agents.items],
  );
  const scenarioDetail = useQuery({
    enabled: editingScenarioId != null,
    queryKey: queryKeys.scenarios.detail(editingScenarioId ?? "pending"),
    queryFn: async () => {
      if (editingScenarioId == null) {
        throw new Error("Scenario id is required.");
      }
      const { data, error } = await apiClient.GET("/v1/scenarios/{id}", {
        params: { path: { id: editingScenarioId } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    initialData: () =>
      queryClient
        .getQueryData<InfiniteData<ListQueryPage<"/v1/scenarios">>>(
          scenariosListKey,
        )
        ?.pages.flatMap((page) => page.items)
        .find((scenario) => scenario.id === editingScenarioId),
  });

  // A drawer subject never crosses an ACTIVE agent scope: a resolved subject
  // whose agent_id mismatches the scope is treated as not-found. Unscoped
  // pages accept any scenario in the org.
  const drawer: ScenarioDrawerState | null = isCreateOpen
    ? { mode: "create" }
    : editingScenarioId == null
      ? null
      : scenarioDetail.data !== undefined
        ? agentId === undefined || scenarioDetail.data.agent_id === agentId
          ? { mode: "edit", scenario: scenarioDetail.data }
          : { message: "Scenario not found for this agent.", mode: "error" }
        : scenarioDetail.isError
          ? {
              message: requestErrorDescription(scenarioDetail.error),
              mode: "error",
            }
          : { mode: "loading" };

  const saveScenario = useMutation({
    mutationFn: async ({
      scenario,
      submission,
    }: {
      scenario: ScenarioResponse | null;
      submission: ScenarioFormSubmission;
    }) => {
      const content = {
        instruction: submission.instruction,
        ...(submission.goal === undefined ? {} : { goal: submission.goal }),
      };
      if (scenario) {
        const body = {
          content,
          environment: submission.environment,
          mode: "generative",
        } satisfies ScenarioRevise;
        const { data, error } = await apiClient.POST(
          "/v1/scenarios/{id}:revise",
          { body, params: { path: { id: scenario.id } } },
        );
        if (error) {
          throw new ApiRequestError(error);
        }
        return data;
      }
      const body = {
        agent_id: submission.agentId,
        content,
        display_name: submission.displayName,
        environment: submission.environment,
        mode: "generative",
      } satisfies ScenarioCreate;
      const { data, error } = await apiClient.POST("/v1/scenarios", { body });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    onSuccess: async (scenario) => {
      onCloseEditor();
      queryClient.setQueryData(
        queryKeys.scenarios.detail(scenario.id),
        scenario,
      );
      toast.success("Scenario saved");
      await queryClient.invalidateQueries({
        queryKey: queryKeys.scenarios.all,
      });
    },
    onError: (error) => {
      toast.error("Scenario save failed", {
        description: requestErrorDescription(error),
      });
    },
  });
  const deleteScenario = useMutation({
    mutationFn: async (scenario: ScenarioResponse) => {
      const { error } = await apiClient.DELETE("/v1/scenarios/{id}", {
        params: { path: { id: scenario.id } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return scenario;
    },
    onSuccess: async () => {
      setDeleteScenarioId(null);
      toast.success("Scenario deleted");
      await queryClient.invalidateQueries({
        queryKey: queryKeys.scenarios.all,
      });
    },
    onError: (error) => {
      toast.error("Scenario delete failed", {
        description: requestErrorDescription(error),
      });
    },
  });

  const agentOptions: ScenarioAgentOption[] | undefined =
    agentId === undefined
      ? agents.items.map((agent) => ({
          id: agent.id,
          label: agent.display_name,
        }))
      : undefined;

  return (
    <ScenariosListView
      agentNamesById={agentNamesById}
      agentOptions={agentOptions}
      deleteError={
        deleteScenario.isError
          ? requestErrorDescription(deleteScenario.error)
          : null
      }
      deleteScenarioId={deleteScenarioId}
      drawer={drawer}
      isDeletePending={deleteScenario.isPending}
      isSavePending={saveScenario.isPending}
      scenarios={scenarios}
      scopedAgentId={agentId}
      saveError={
        saveScenario.isError
          ? requestErrorDescription(saveScenario.error)
          : null
      }
      onCancelDelete={() => {
        deleteScenario.reset();
        setDeleteScenarioId(null);
      }}
      onCloseEditor={onCloseEditor}
      onConfirmDelete={(scenario) => {
        deleteScenario.mutate(scenario);
      }}
      onOpenEdit={onOpenEdit}
      onSave={async (submission) => {
        await saveScenario.mutateAsync({
          scenario: drawer?.mode === "edit" ? drawer.scenario : null,
          submission,
        });
      }}
      onStartDelete={(scenario) => {
        deleteScenario.reset();
        setDeleteScenarioId(scenario.id);
      }}
    />
  );
}
